import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { setFullMediaTranslation } from 'js/src/agent/video-state';
import { isNonRecommendable } from 'js/src/agent/video-state/serialization/assetSerializer';
import { setBrightnessUserOverride, setCropUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyUserMediaKenburnOperation,
    applyUserMediaTrimOperation,
} from 'js/src/luminary/luminary-operations';

const PLAYBACK_MODES = ['loop', 'hold'] as const;
type PlaybackModeArg = (typeof PLAYBACK_MODES)[number];

// Mirrors the manual advanced-media-controls speed slider (0.1x–3x).
const MIN_PLAYBACK_RATE = 0.1;
const MAX_PLAYBACK_RATE = 3;

export const editMediaSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. media_1)'),
    scale: z
        .number()
        .min(10)
        .max(1000)
        .optional()
        .describe('Scale value for the media in percentage (zoom). Above 100 zooms in.'),
    position: z
        .object({
            x: z.number().meta({ description: 'X value in pixels.', default: 0 }),
            y: z.number().meta({ description: 'Y value in pixels.', default: 0 }),
        })
        .optional()
        .describe(
            "Translate (pan) the media relative to the center of the asset's bounds. The translation range is determined by the asset's mediaXTranslationRange and mediaYTranslationRange property. If the range is too narrow, increase the media's scale.",
        ),
    kenburn: z
        .enum(VideoData.ALL_BACKGROUND_ANIMATIONS)
        .optional()
        .describe(
            'Ken Burns motion for a still image (video clips always play with none). Vary it across consecutive image scenes rather than defaulting to zoom_in: alternate zoom_in/zoom_out, and pan in the direction the subject faces or moves.',
        ),
    brightness: z
        .number()
        .min(0)
        .max(1.5)
        .optional()
        .describe(
            'Brightness level for the media asset (0=completely black, 0.5=dimmed, 1=default, 1.2=bright, 1.5=very bright)',
        ),
    volume: z
        .number()
        .min(0)
        .max(100)
        .optional()
        .describe('Volume level for video assets, from 0 (silent) to 100 (full)'),
    start_time: z
        .number()
        .min(0)
        .optional()
        .describe(
            'Trim in-point: milliseconds into the source clip where playback should start. Video assets only.',
        ),
    end_time: z
        .number()
        .min(0)
        .optional()
        .describe(
            'Trim out-point: milliseconds into the source clip where playback should end. Video assets only.',
        ),
    playback_mode: z
        .enum(PLAYBACK_MODES)
        .optional()
        .describe(
            'How a video that is shorter than its scene behaves: "loop" repeats the clip, "hold" freezes on the last frame. Video assets only.',
        ),
    playback_rate: z
        .number()
        .min(MIN_PLAYBACK_RATE)
        .max(MAX_PLAYBACK_RATE)
        .optional()
        .describe(
            'Playback speed multiplier (1 = normal, 0.5 = half speed, 2 = double speed). Video assets only.',
        ),
});
export type EditMediaArgs = z.infer<typeof editMediaSchema>;

/**
 * Edits an existing media asset in place: how it is framed (scale, position, Ken Burns, brightness),
 * how it plays (trim, loop/hold, speed, volume). It never changes *which* clip is in the slot — use
 * `set_media` for that. Splitting the two keeps each tool's parameters focused: `set_media` picks the
 * clip, `edit_media` adjusts it.
 */
export class EditMediaTool extends VideoStateTool<typeof editMediaSchema> {
    protected override readonly schema = editMediaSchema;
    protected override readonly toolName = TOOL_NAMES.editMedia;
    protected override readonly toolDescription = `Adjust an existing media asset without changing which clip is in the slot: reframe it (scale, position, Ken Burns, brightness) or change how it plays (trim with start_time/end_time, loop-or-hold a short clip, speed, volume).

Scale and position reframe the media inside a fixed frame, and the position axes are inverted — load the \`${SKILL_NAMES.cropMedia}\` skill before using them.`;

    public override getToolDisplayName(): string {
        return 'Editing media';
    }

    public get statusIcon(): string {
        return 'tune';
    }

    private lastEditedSceneIndex: number | undefined;

    protected override onEditApplied(): void {
        // Reframing the first scene changes the thumbnail.
        if (this.lastEditedSceneIndex === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    protected override preprocessArgs(args: EditMediaArgs): EditMediaArgs {
        // position.x/y carry a 0 default that .meta emits but does not fill at parse time, so a
        // partial position like { x: 5 } would hard-fail on the missing y — fill it here.
        if (args.position && typeof args.position === 'object' && !Array.isArray(args.position)) {
            const position = args.position as { x?: number; y?: number };
            args = { ...args, position: { x: 0, y: 0, ...position } };
        }
        return args;
    }

    public async execute(args: EditMediaArgs, context: ToolExecutionContext) {
        const {
            asset_name,
            scale,
            position,
            kenburn,
            brightness,
            volume,
            start_time,
            end_time,
            playback_mode,
            playback_rate,
        } = args;
        const { parentActionGroup } = context;

        const changes: string[] = [];
        const notes: string[] = [];
        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            this.lastEditedSceneIndex = scene_index;

            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId} which uses design ${scene.sceneDesign}`;
            }
            if (!asset.isEditable || isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }
            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a media asset`;
            }

            const wantsPlaybackEdit =
                start_time !== undefined ||
                end_time !== undefined ||
                playback_mode !== undefined ||
                playback_rate !== undefined;

            // The presenter slot of a talking-head scene mirrors the scene group's recording. Its
            // playback is owned by the transcript, so trimming/looping/re-speeding it here would fight
            // the narration, and its audio is the group's voice, which every scene in the group
            // shares — point the agent at the scene-group tools for both. Reframing still applies.
            if (asset.linkToSceneGroup && wantsPlaybackEdit) {
                return `Error: ${asset_name} in scene ${sceneShortId} is the talking head — its playback follows the speech and cannot be trimmed, looped or re-timed here. Use ${TOOL_NAMES.trimSceneGroup} to shorten the recording instead. Reframing (scale/position) still works through ${TOOL_NAMES.editMedia}; the recording's volume is set with ${TOOL_NAMES.setSceneGroupVolume}.`;
            }
            if (asset.linkToSceneGroup && volume !== undefined) {
                return `Error: ${asset_name} in scene ${sceneShortId} is the talking head — its audio is the scene group's voice, shared by every scene in the group. Set it with ${TOOL_NAMES.setSceneGroupVolume} (voice_volume) instead. Reframing (scale/position) still works through ${TOOL_NAMES.editMedia}.`;
            }

            // Validate the trim BEFORE mutating anything, so an invalid trim can't return an error
            // after reframe edits have already been written (they would be left applied and, since the
            // final checkpoint never runs, uncheckpointed). Trim is video-only; an image trim is
            // skipped with a note further down, so it needs no validation here.
            const wantsTrim = start_time !== undefined || end_time !== undefined;
            let resolvedTrim: { start: number; end: number } | null = null;
            if (wantsTrim && asset.isVideo) {
                const trim = this.resolveTrim(asset, start_time, end_time);
                if ('error' in trim) {
                    return `Error: ${trim.error}`;
                }
                resolvedTrim = trim;
            }

            const { applyUserMediaKenburn, applyUserMediaTrim } = makeOperationsCallable(
                { applyUserMediaKenburnOperation, applyUserMediaTrimOperation },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            if (kenburn !== undefined) {
                applyUserMediaKenburn(scene.id, asset.id, kenburn, false, parentActionGroup);
                changes.push(`media animation to ${kenburn}`);
            }

            if (brightness !== undefined) {
                asset.brightness = brightness;
                this.dispatch(
                    setBrightnessUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                changes.push(`media brightness to ${brightness}`);
            }

            if (scale !== undefined) {
                const scaleValue = scale / 100;
                asset.scaleX = scaleValue;
                asset.scaleY = scaleValue;
                changes.push('media scale');
            }
            if (position !== undefined) {
                setFullMediaTranslation(asset, position.x, position.y);
                const xDirection = position.x === 0 ? 'center' : position.x < 0 ? 'right' : 'left';
                const yDirection = position.y === 0 ? 'center' : position.y < 0 ? 'down' : 'up';
                changes.push(
                    `media position to ${position.x} (${xDirection}), ${position.y} (${yDirection})`,
                );
            }
            // Scale and position are crop transformations — register the crop user override so a later
            // media swap's recommendation leaves the agent's framing alone.
            if (scale !== undefined || position !== undefined) {
                this.dispatch(
                    setCropUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            // Volume, trim, loop/hold and speed only mean something on a video. Skip them (with a
            // note) for an image rather than rejecting the whole call, so the reframe edits still land.
            if (volume !== undefined) {
                if (asset.isVideo) {
                    asset.volume = volume;
                    changes.push(`media volume to ${volume}`);
                } else {
                    notes.push(`volume was ignored because ${asset_name} contains an image`);
                }
            }

            if (wantsPlaybackEdit && !asset.isVideo) {
                notes.push(
                    `trim/loop/speed were ignored because ${asset_name} contains an image, which does not play`,
                );
            } else if (wantsPlaybackEdit) {
                if (resolvedTrim) {
                    applyUserMediaTrim(
                        scene.id,
                        asset_name,
                        resolvedTrim.start,
                        resolvedTrim.end,
                        false,
                        parentActionGroup,
                    );
                    changes.push(`trim to ${resolvedTrim.start}–${resolvedTrim.end}ms of the clip`);
                }
                if (playback_mode !== undefined) {
                    asset.playbackMode = this.toPlaybackMode(playback_mode);
                    changes.push(`playback to ${playback_mode}`);
                }
                if (playback_rate !== undefined) {
                    asset.playbackRate = playback_rate;
                    changes.push(`speed to ${playback_rate}x`);
                }
            }

            const noteText = notes.length > 0 ? ` NOTE: ${notes.join('; ')}.` : '';
            if (changes.length === 0) {
                return `Success: Nothing was changed for asset ${asset_name} in scene ${sceneShortId}.${noteText}`;
            }

            // Share parentActionGroup so every dispatch above (overrides, the kenburn/trim operations'
            // own checkpoints) and this one collapse into a single undo step.
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            this.recordSceneFill(scene_index, 'media');

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${sceneShortId}.${noteText}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error editing media: ${errorMessage}`;
        }
    }

    private toPlaybackMode(mode: PlaybackModeArg): string {
        return mode === 'loop' ? VideoData.PLAYBACK_LOOP : VideoData.PLAYBACK_HOLD;
    }

    /**
     * Resolve the trim window, filling whichever end the caller left out from the asset's current
     * trim (or the full source), then validating it against the source duration when known.
     */
    private resolveTrim(
        asset: TimeAwareMediaAsset,
        startTime: number | undefined,
        endTime: number | undefined,
    ): { start: number; end: number } | { error: string } {
        const sourceDuration = asset.sourceDuration;
        const start = Math.round(startTime ?? asset.startTime ?? 0);
        const resolvedEnd = endTime ?? asset.endTime ?? sourceDuration ?? undefined;
        if (resolvedEnd == null) {
            return {
                error: 'could not determine the clip end — pass end_time (milliseconds into the clip)',
            };
        }
        const end = Math.round(resolvedEnd);
        if (start < 0 || end <= start) {
            return {
                error: `invalid trim: start_time (${start}ms) must be less than end_time (${end}ms)`,
            };
        }
        if (sourceDuration != null && end > Math.round(sourceDuration)) {
            return {
                error: `end_time (${end}ms) is past the clip's ${Math.round(sourceDuration)}ms duration`,
            };
        }
        return { start, end };
    }
}
